import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion, useInView } from "framer-motion";
import { safeJsonLd } from "./utils/safeJsonLd";
import {
  ArrowRight, Menu, X, Heart, Users, Stethoscope,
  Apple, Shield, Globe, ChevronDown, Sparkles, Target,
  MessageCircle, Activity, Facebook, Instagram, Youtube,
  Flower2, Moon, Clock, Baby, Leaf, CircleDot
} from "lucide-react";

const Anim = ({ children, className = "", delay = 0 }) => {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.65, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}>{children}</motion.div>
  );
};

const WHATSAPP_PHONE = "919108818369";

const SectionHeading = ({ eyebrow, heading, children, light = false }) => (
  <Anim>
    <div className="text-center max-w-3xl mx-auto mb-14 md:mb-16">
      {eyebrow && <span className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">{eyebrow}</span>}
      <h2 className={`font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight mt-3 ${light ? "text-bone" : "text-emerald_deep"}`}>{heading}</h2>
      {children && <div className={`mt-4 text-base leading-relaxed max-w-2xl mx-auto ${light ? "text-bone/70" : "text-text_muted"}`}>{children}</div>}
    </div>
  </Anim>
);

const Card = ({ icon: Icon, title, children }) => (
  <div className="bg-surface border border-border_custom p-6 md:p-8 rounded-xl hover:shadow-md transition-shadow h-full">
    {Icon && <div className="w-11 h-11 rounded-xl bg-accent/10 flex items-center justify-center mb-4"><Icon size={20} className="text-accent" /></div>}
    <h3 className="font-serif text-lg md:text-xl text-emerald_deep mb-3">{title}</h3>
    <div className="text-sm text-text_muted leading-relaxed">{children}</div>
  </div>
);

const FAQItem = ({ q, a, open, onToggle }) => (
  <div className="border-b border-border_custom">
    <button onClick={onToggle} className="w-full flex items-center justify-between py-5 text-left group" aria-expanded={open}>
      <span className="font-serif text-base md:text-lg text-emerald_deep pr-4 group-hover:text-accent transition-colors">{q}</span>
      <ChevronDown size={18} className={`text-text_muted flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180 text-accent" : ""}`} />
    </button>
    <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
      transition={{ duration: 0.3 }} className="overflow-hidden">
      <p className="text-sm text-text_muted leading-relaxed pb-5">{a}</p>
    </motion.div>
  </div>
);

const LandingHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);
  const navItems = [
    { label: "Home", href: "/" },
    { label: "Fertility Nutrition", href: "/fertility-nutrition" },
    { label: "Female Fertility", href: "/female-fertility-nutrition" },
    { label: "Programs", href: "/#programs" },
    { label: "Team", href: "/#team" },
    { label: "Contact", href: "/#contact" }
  ];
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-bone/95 backdrop-blur-xl shadow-sm" : "bg-transparent"}`} data-testid="ff-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="/" className="font-serif text-2xl tracking-tight">
            <span className={`font-semibold ${scrolled ? "text-emerald_deep" : "text-bone"}`}>Fitly</span>
            <span className={`font-light ml-1 ${scrolled ? "text-text_muted" : "text-bone/60"}`}>India</span>
          </a>
          <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            {navItems.map(item => (
              <a key={item.label} href={item.href} className={`hover:text-accent transition-colors text-sm tracking-wide ${scrolled ? "text-text_muted" : "text-bone/70"}`}>{item.label}</a>
            ))}
          </nav>
          <div className="hidden md:block">
            <a href="/consultation" className="bg-accent text-emerald_deep px-7 py-2.5 text-sm tracking-wide font-semibold hover:bg-accent_light transition-all inline-flex items-center gap-2 rounded-full">
              Book Consultation <ArrowRight size={14} />
            </a>
          </div>
          <button onClick={() => setMenuOpen(!menuOpen)} className={`md:hidden ${scrolled ? "text-emerald_deep" : "text-bone"}`}>
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden py-6 bg-surface border border-border_custom rounded-xl mt-2 mb-4 shadow-lg">
            {navItems.map(item => (
              <a key={item.label} href={item.href} className="block w-full text-left px-6 py-3 text-text_muted hover:text-emerald_deep hover:bg-bone/50 text-sm tracking-wide">{item.label}</a>
            ))}
            <div className="px-6 pt-3">
              <a href="/consultation" className="block w-full bg-emerald_deep text-bone py-3 text-sm tracking-wide text-center rounded-full">Book Consultation</a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

const LandingFooter = () => (
  <footer className="bg-emerald_deep text-bone py-16 relative">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
        <div className="md:col-span-2">
          <div className="font-serif text-3xl mb-4"><span className="text-accent">Fitly</span> India</div>
          <p className="text-bone/60 leading-relaxed max-w-sm">Fertility Nutrition & Conception Care for women, men and couples across India and internationally.</p>
          <div className="flex gap-4 mt-6">
            {[[Facebook, "https://www.facebook.com/294056110466070"], [Instagram, "https://www.instagram.com/fitly_india"], [Youtube, "https://www.youtube.com/fitly_india"]].map(([Icon, href]) => (
              <a key={href} href={href} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-bone/10 flex items-center justify-center hover:bg-accent/20 transition-colors"><Icon size={16} className="text-bone/70" /></a>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase font-semibold text-accent mb-4">Quick Links</h4>
          <ul className="space-y-2">
            {[["Home", "/"], ["Fertility Nutrition", "/fertility-nutrition"], ["Female Fertility", "/female-fertility-nutrition"], ["Male Fertility", "/male-fertility-nutrition"], ["Consultation", "/consultation"]].map(([label, href]) => (
              <li key={label}><a href={href} className="text-bone/60 hover:text-accent transition-colors text-sm">{label}</a></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase font-semibold text-accent mb-4">Contact</h4>
          <ul className="space-y-2 text-sm text-bone/60">
            <li><a href="tel:+919108818369" className="hover:text-accent transition-colors">+91 91088 18369</a></li>
            <li><a href="mailto:fitlyindiahealth@gmail.com" className="hover:text-accent transition-colors">fitlyindiahealth@gmail.com</a></li>
            <li>Mon - Sat, 10 AM - 6 PM</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-bone/10 pt-8 text-center text-bone/40 text-xs">&copy; {new Date().getFullYear()} Fitly India. All rights reserved.</div>
    </div>
  </footer>
);

const faqData = [
  { q: "Can nutrition improve egg quality?", a: "A nutrient-dense diet rich in antioxidants, healthy fats, adequate protein and essential micronutrients can support the cellular environment in which eggs develop over approximately 90 days. While no food can reverse age-related ovarian decline, optimising nutrition is a reasonable step toward supporting overall reproductive health." },
  { q: "Can food increase AMH?", a: "AMH is primarily a marker of ovarian reserve reflecting the number of remaining follicles. No food, supplement or diet has been proven to increase AMH levels. Fitly focuses on overall nutritional and reproductive health rather than making AMH-specific promises." },
  { q: "Can nutrition help with PCOS fertility?", a: "Yes. Nutrition plays a well-recognised role in PCOS management. Improving insulin sensitivity, managing weight, correcting nutritional deficiencies and adopting sustainable dietary patterns can support metabolic health and menstrual regularity, which are important factors in PCOS-related fertility." },
  { q: "How does endometriosis affect fertility?", a: "Endometriosis can affect fertility through inflammation, adhesions and ovarian involvement. While nutrition cannot cure endometriosis, personalised dietary strategies focused on adequate nourishment and anti-inflammatory support can complement medical management." },
  { q: "What nutrition is important before IVF?", a: "Before IVF, focus on correcting identified nutritional deficiencies (folate, iron, vitamin D, omega-3s), achieving a healthy weight, consuming antioxidant-rich foods and establishing sustainable eating patterns. Fitly creates personalised pre-IVF plans tailored to individual needs." },
  { q: "When should I start preconception nutrition?", a: "Ideally three to six months before trying to conceive, to allow time to correct deficiencies and support the approximately 90-day egg maturation window. However, starting at any point is beneficial." },
  { q: "Do you provide nutrition for irregular periods?", a: "Yes. Irregular cycles and ovulation concerns are commonly addressed through nutritional and lifestyle support. Fitly assesses underlying factors such as weight, stress, nutritional status and hormonal patterns to develop a personalised strategy." },
  { q: "Is the consultation done online?", a: "Yes. All Fitly consultations are conducted online via video or voice call. Clients from anywhere in India or internationally can access personalised fertility nutrition support." },
  { q: "Can I join with my husband?", a: "Absolutely. Fitly offers couple programs where both partners receive individualised nutrition plans. This is often the most effective approach since conception depends on both partners' health." },
  { q: "Does nutrition replace fertility treatment?", a: "No. Nutrition supports overall reproductive health but does not replace medical fertility treatment. Fitly encourages appropriate medical evaluation and works alongside your treating fertility specialist." }
];

const structuredData = {
  faq: { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": faqData.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) },
  breadcrumb: { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://fitlyindia.org/" },
    { "@type": "ListItem", "position": 2, "name": "Fertility Nutrition", "item": "https://fitlyindia.org/fertility-nutrition/" },
    { "@type": "ListItem", "position": 3, "name": "Female Fertility Nutrition", "item": "https://fitlyindia.org/female-fertility-nutrition/" }
  ]},
  service: { "@context": "https://schema.org", "@type": "Service", "name": "Female Fertility Nutrition Consultation", "provider": { "@type": "Organization", "name": "Fitly India", "url": "https://fitlyindia.org", "telephone": "+919108818369" }, "serviceType": "Female Fertility Nutrition", "areaServed": { "@type": "Country", "name": "India" }, "description": "Personalised female fertility nutrition for PCOS, egg quality, endometriosis, low AMH, ovulation health and IVF preparation." }
};

export default function FemaleFertilityNutritionPage() {
  const [openFaq, setOpenFaq] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-bone text-text_main" data-testid="female-fertility-page">
      <Helmet>
        <title>Female Fertility Nutritionist in India | PCOS, Egg Quality, IVF | Fitly India</title>
        <meta name="description" content="Personalised female fertility nutrition for PCOS, egg quality, endometriosis, low AMH and IVF preparation. Online fertility nutritionist for women in India from Fitly India." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://fitlyindia.org/female-fertility-nutrition/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fitlyindia.org/female-fertility-nutrition/" />
        <meta property="og:title" content="Female Fertility Nutritionist in India | Fitly India" />
        <meta property="og:description" content="Personalised female fertility nutrition for PCOS, egg quality, endometriosis, low AMH and IVF preparation from Fitly India." />
        <meta property="og:site_name" content="Fitly India" />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(structuredData.faq) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(structuredData.breadcrumb) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(structuredData.service) }} />

      <LandingHeader />

      <main>
        {/* HERO */}
        <section className="relative min-h-screen flex items-center bg-emerald_deep pt-20 overflow-hidden" data-testid="ff-hero">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, #D4AF37 1px, transparent 1px), radial-gradient(circle at 80% 20%, #D4AF37 1px, transparent 1px)", backgroundSize: "60px 60px" }}></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full relative z-10 py-16 md:py-24">
            <div className="max-w-3xl">
              <Anim>
                <nav aria-label="Breadcrumb" className="mb-8">
                  <ol className="flex items-center gap-2 text-xs text-bone/40">
                    <li><a href="/" className="hover:text-accent transition-colors">Home</a></li><li>/</li>
                    <li><a href="/fertility-nutrition" className="hover:text-accent transition-colors">Fertility Nutrition</a></li><li>/</li>
                    <li className="text-accent">Female Fertility</li>
                  </ol>
                </nav>
              </Anim>
              <Anim delay={0.1}>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight text-bone mb-6">
                  Female Fertility Nutrition<br /><span className="italic font-light text-accent">in India</span>
                </h1>
              </Anim>
              <Anim delay={0.2}>
                <p className="text-lg md:text-xl text-bone/80 leading-relaxed mb-6 max-w-2xl">
                  Personalised Nutrition for PCOS, Egg Quality, Endometriosis, Low AMH, Ovulation Health and Preconception Wellness
                </p>
              </Anim>
              <Anim delay={0.3}>
                <p className="text-base text-bone/60 leading-relaxed mb-4">
                  Female fertility is influenced by age, underlying medical conditions, ovulation patterns, ovarian reserve, metabolic health, nutritional status and lifestyle. At Fitly India, we go beyond generic diet charts to create personalised fertility nutrition plans that address the unique factors affecting your reproductive health.
                </p>
              </Anim>
              <Anim delay={0.4}>
                <div className="flex flex-wrap gap-3 mb-10">
                  {["PCOS & Fertility", "Egg Quality", "Low AMH", "Endometriosis", "IVF Preparation", "Ovulation Health"].map(tag => (
                    <span key={tag} className="border border-accent/30 text-accent text-xs tracking-wider px-4 py-2 rounded-full">{tag}</span>
                  ))}
                </div>
              </Anim>
              <Anim delay={0.5}>
                <a href="/consultation" data-testid="ff-hero-cta" className="inline-flex items-center gap-2 bg-accent text-emerald_deep px-8 py-3.5 text-sm tracking-wide font-semibold hover:bg-accent_light transition-all rounded-full">
                  Book a Female Fertility Consultation <ArrowRight size={16} />
                </a>
              </Anim>
            </div>
          </div>
        </section>

        {/* CONDITIONS WE SUPPORT */}
        <section className="py-20 md:py-28 bg-bone" data-testid="ff-conditions">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <SectionHeading eyebrow="Conditions We Support" heading="Fertility Nutrition for Women's Health">
              <p>Every woman's fertility journey is different. Fitly provides nutrition support for a range of conditions that can affect reproductive health.</p>
            </SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Activity, title: "PCOS & Fertility", text: "Nutrition supporting metabolic health, insulin sensitivity, nutritional adequacy and sustainable lifestyle changes. PCOS is one of the most common causes of ovulatory difficulty, and personalised dietary management plays a key role." },
                { icon: Sparkles, title: "Egg Quality & Preconception", text: "Nutrient-dense nutrition addressing antioxidant status, essential fatty acids, folate, iron and other key nutrients during the 90-day egg maturation window. Optimising your nutritional foundation before conception." },
                { icon: Shield, title: "Low AMH & Diminished Reserve", text: "AMH is primarily a marker of ovarian reserve — no food or diet can restore it. Fitly focuses on overall nutritional and reproductive health alongside appropriate medical fertility care." },
                { icon: Heart, title: "Endometriosis & Fertility", text: "Personalised nutrition focused on adequate nourishment, anti-inflammatory support and overall health alongside medical care. Endometriosis management benefits from a holistic approach." },
                { icon: Moon, title: "Ovulation & Menstrual Health", text: "Nutrition and lifestyle support for cycle regularity, adequate luteal phase and overall menstrual wellness. Regular ovulation is a key marker of reproductive health." },
                { icon: Stethoscope, title: "Thyroid & Hormonal Balance", text: "Nutritional support for thyroid function, hormonal balance and metabolic health. Thyroid disorders and hormonal imbalances can significantly impact fertility." }
              ].map((card, idx) => (
                <Anim key={card.title} delay={idx * 0.08}>
                  <Card icon={card.icon} title={card.title}>{card.text}</Card>
                </Anim>
              ))}
            </div>
          </div>
        </section>

        {/* THE 90-DAY WINDOW */}
        <section className="py-20 md:py-28 bg-emerald_deep relative" data-testid="ff-egg-health">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 70% 30%, #D4AF37 1px, transparent 1px)", backgroundSize: "50px 50px" }}></div>
          <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">
            <SectionHeading eyebrow="The Egg Health Window" heading="Nutrition During the 90-Day Egg Maturation Cycle" light>
              <p>Eggs develop over approximately 90 days before ovulation. This window is a valuable opportunity to influence the nutritional environment in which your eggs mature.</p>
            </SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { icon: Leaf, title: "Antioxidant Protection", text: "Vitamins C, E, selenium and CoQ10-supportive foods help combat oxidative stress that can affect egg quality during development." },
                { icon: CircleDot, title: "Healthy Fats & Hormones", text: "Omega-3 fatty acids and balanced fat intake support hormonal signalling, cell membrane integrity and the inflammatory environment." },
                { icon: Target, title: "Micronutrient Adequacy", text: "Folate, iron, vitamin D, iodine and zinc are essential for reproductive health. Deficiencies are identified and addressed through diet-first strategies." },
                { icon: Flower2, title: "Blood Sugar & Insulin", text: "Stable blood sugar and insulin sensitivity are crucial, especially for women with PCOS. Personalised carbohydrate management supports metabolic and reproductive health." }
              ].map((card, idx) => (
                <Anim key={card.title} delay={idx * 0.1}>
                  <div className="bg-bone/5 border border-bone/10 p-6 md:p-8 rounded-xl hover:bg-bone/10 transition-colors">
                    <card.icon size={22} className="text-accent mb-4" />
                    <h3 className="font-serif text-lg text-bone mb-3">{card.title}</h3>
                    <p className="text-sm text-bone/65 leading-relaxed">{card.text}</p>
                  </div>
                </Anim>
              ))}
            </div>
          </div>
        </section>

        {/* IVF PREPARATION */}
        <section className="py-20 md:py-28 bg-bone" data-testid="ff-ivf">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <SectionHeading eyebrow="IVF Preparation" heading="Nutrition Before IVF for Women">
              <p>Entering your IVF cycle well-nourished can support your body's readiness for treatment.</p>
            </SectionHeading>
            <Anim>
              <div className="bg-surface border border-border_custom rounded-xl p-8 text-left max-w-2xl mx-auto mb-8">
                <p className="text-sm text-text_muted leading-relaxed mb-4">
                  Fitly's pre-IVF nutrition focuses on correcting identified nutritional deficiencies, supporting metabolic health, optimising weight where needed, and establishing sustainable eating habits before your treatment cycle.
                </p>
                <p className="text-sm text-text_muted leading-relaxed">
                  All nutrition support works alongside your fertility specialist's protocol. Medication decisions and IVF treatment remain under the treating physician.
                </p>
              </div>
            </Anim>
            <Anim delay={0.1}>
              <a href="/consultation" className="inline-flex items-center gap-2 bg-emerald_deep text-bone px-8 py-3 text-sm tracking-wide font-medium hover:bg-emerald_deep/90 transition-all rounded-full">
                Explore IVF Nutrition Support <ArrowRight size={14} />
              </a>
            </Anim>
          </div>
        </section>

        {/* COUPLE CONTEXT */}
        <section className="py-20 md:py-28 bg-surface_alt" data-testid="ff-couple">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <SectionHeading eyebrow="Better Together" heading="Join as a Couple">
              <p>Conception depends on both partners. Fitly's couple programs provide personalised nutrition for both her and him, working toward a shared preconception goal.</p>
            </SectionHeading>
            <Anim>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/male-fertility-nutrition" className="inline-flex items-center gap-2 border border-emerald_deep/20 text-emerald_deep px-8 py-3 text-sm font-medium hover:bg-emerald_deep/5 transition-all rounded-full">
                  Explore Male Fertility Nutrition <ArrowRight size={14} />
                </a>
                <a href="/fertility-nutrition" className="inline-flex items-center gap-2 text-accent text-sm font-medium hover:underline px-4 py-3">
                  View Full Fertility Nutrition Approach <ArrowRight size={14} />
                </a>
              </div>
            </Anim>
          </div>
        </section>

        {/* WHY FITLY */}
        <section className="py-20 md:py-28 bg-emerald_deep relative" data-testid="ff-why-fitly">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 40% 60%, #D4AF37 1px, transparent 1px)", backgroundSize: "50px 50px" }}></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <SectionHeading eyebrow="The Fitly Difference" heading="Why Choose Fitly for Female Fertility Nutrition?" light />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Baby, title: "Fertility-Focused", text: "Programs designed specifically around female reproductive health — not generic diet plans with a fertility label." },
                { icon: Stethoscope, title: "Multidisciplinary Team", text: "Mrs. Nadiya (Deep Health Coach, Precision Nutrition L2), Mrs. Revathy R.P (Clinical Nutritionist, Certified in Fertility Nutrition), alongside gynaecologists and Ayurveda specialists." },
                { icon: Target, title: "Personalised Plans", text: "Every plan reflects your dietary preferences, health history, reports, condition and individual nutritional requirements." },
                { icon: Apple, title: "Indian Food. Real Life.", text: "Practical, familiar foods — not exotic fertility superfoods. Sustainable nutrition that fits your culture and daily routine." },
                { icon: Users, title: "Couple Programs", text: "Both partners can join together with individualised plans addressing female and male factors simultaneously." },
                { icon: Globe, title: "Online Across India", text: "Video or voice consultations with digital support. Available for women across India and internationally." }
              ].map((card, idx) => (
                <Anim key={card.title} delay={idx * 0.08}>
                  <div className="bg-bone/5 border border-bone/10 p-6 md:p-8 rounded-xl hover:bg-bone/10 transition-colors h-full">
                    <card.icon size={22} className="text-accent mb-4" />
                    <h3 className="font-serif text-lg text-bone mb-3">{card.title}</h3>
                    <p className="text-sm text-bone/65 leading-relaxed">{card.text}</p>
                  </div>
                </Anim>
              ))}
            </div>
          </div>
        </section>

        {/* APPROACH */}
        <section className="py-20 md:py-28 bg-bone" data-testid="ff-approach">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <SectionHeading eyebrow="Our Process" heading="How Female Fertility Nutrition Works at Fitly" />
            {[
              { num: "01", title: "Understand", text: "Your fertility journey, medical history, menstrual patterns, lifestyle, food habits and goals through an in-depth consultation." },
              { num: "02", title: "Assess", text: "Review hormonal reports, nutritional markers, dietary patterns and lifestyle factors to identify areas nutrition can improve." },
              { num: "03", title: "Personalise", text: "An individual nutrition strategy addressing your specific condition — PCOS, egg quality, endometriosis, IVF prep or general preconception health." },
              { num: "04", title: "Support", text: "Ongoing guidance, adjustments and accountability throughout the program as your body responds and your journey progresses." },
              { num: "05", title: "Collaborate", text: "Medical referral when clinical management is needed. Nutrition complements — never replaces — medical care." }
            ].map((step, idx) => (
              <Anim key={step.num} delay={idx * 0.1}>
                <div className="flex gap-6 md:gap-10 py-8 border-b border-border_custom group">
                  <span className="font-serif text-4xl md:text-5xl text-accent/20 group-hover:text-accent transition-colors flex-shrink-0">{step.num}</span>
                  <div>
                    <h3 className="font-serif text-xl md:text-2xl text-emerald_deep mb-2 group-hover:text-accent transition-colors">{step.title}</h3>
                    <p className="text-sm text-text_muted leading-relaxed max-w-xl">{step.text}</p>
                  </div>
                </div>
              </Anim>
            ))}
          </div>
        </section>

        {/* TRUST */}
        <section className="py-20 md:py-28 bg-surface_alt" data-testid="ff-trust">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <Anim>
              <span className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">Medical Responsibility</span>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-emerald_deep mt-3 mb-8">
                Nutrition Is One Part of <span className="italic font-light">the Picture</span>
              </h2>
            </Anim>
            <Anim delay={0.1}>
              <div className="space-y-5 text-base text-text_muted leading-relaxed text-left">
                <p>Female infertility can have many causes — structural, hormonal, age-related or unexplained — and some require medical evaluation and treatment.</p>
                <p>No food, supplement or fertility diet can guarantee pregnancy or replace clinical management.</p>
                <p>Fitly's goal is to help women optimise the nutritional and lifestyle factors within their control while complementing appropriate medical care from their treating specialist.</p>
              </div>
            </Anim>
            <Anim delay={0.2}><div className="gold-divider w-32 mx-auto mt-10"></div></Anim>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 md:py-28 bg-bone" data-testid="ff-faq">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <SectionHeading eyebrow="Common Questions" heading="Female Fertility Nutrition FAQ" />
            <div className="bg-surface border border-border_custom rounded-xl p-6 md:p-8">
              {faqData.map((faq, idx) => (
                <FAQItem key={faq.q} q={faq.q} a={faq.a} open={openFaq === idx} onToggle={() => setOpenFaq(openFaq === idx ? null : idx)} />
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-20 md:py-28 bg-emerald_deep relative" data-testid="ff-final-cta">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 60% 40%, #D4AF37 1px, transparent 1px)", backgroundSize: "50px 50px" }}></div>
          <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10 text-center">
            <Anim><h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-bone mb-6">Your Body. Your Journey.<br /><span className="italic font-light text-accent">Your Nutrition Plan.</span></h2></Anim>
            <Anim delay={0.1}><p className="text-base text-bone/70 leading-relaxed max-w-2xl mx-auto mb-10">Whether you're managing PCOS, concerned about egg quality, preparing for IVF, or simply want to optimise your preconception health, Fitly can help you build a personalised nutrition strategy.</p></Anim>
            <Anim delay={0.2}>
              <div className="mb-10">
                <p className="font-serif text-2xl text-bone mb-1"><span className="text-accent">Fitly</span> India</p>
                <p className="text-sm text-bone/60 tracking-wider uppercase mb-1">Female Fertility Nutrition</p>
                <p className="text-xs text-accent tracking-wider">PCOS. Egg Quality. Endometriosis. IVF. Preconception.</p>
              </div>
            </Anim>
            <Anim delay={0.3}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/consultation" data-testid="ff-final-cta-book" className="inline-flex items-center justify-center gap-2 bg-accent text-emerald_deep px-10 py-3.5 text-sm tracking-wide font-semibold hover:bg-accent_light transition-all rounded-full">Book Your Consultation <ArrowRight size={16} /></a>
                <a href={`https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent("Hi! I'd like to learn more about Fitly India's female fertility nutrition programs.")}`} target="_blank" rel="noopener noreferrer" data-testid="ff-final-cta-whatsapp" className="inline-flex items-center justify-center gap-2 border border-bone/20 text-bone px-8 py-3.5 text-sm tracking-wide hover:bg-bone/10 transition-all rounded-full"><MessageCircle size={16} /> Message on WhatsApp</a>
              </div>
            </Anim>
          </div>
        </section>

        <aside className="py-10 bg-bone border-t border-border_custom" data-testid="ff-disclaimer">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <p className="text-xs text-text_muted/70 leading-relaxed text-center">
              <strong className="text-text_muted">Medical Disclaimer:</strong> Information on this page is for educational purposes and does not constitute diagnosis or medical treatment. Fertility conditions require evaluation by qualified healthcare professionals. Nutrition and lifestyle interventions cannot guarantee conception or IVF success and should not replace medical treatment.
            </p>
          </div>
        </aside>
      </main>

      <LandingFooter />
    </div>
  );
}
